import { FaroText, TranslateVar } from "@faro-lotv/flat-ui";
import {
  BcfBackendApiErrorDetails,
  BcfBackendApiErrorDetailsType,
} from "@faro-lotv/service-wires";
import { Box } from "@mui/material";
import type { JSX } from "react";
import {
  getExternalAnnotationProviderName,
  KNOWN_BCF_BACKEND_CORE_API_ERROR_CODES,
} from "./external-annotation-utils";
import {
  ProviderAuthorization,
  ProviderAuthorizationProps,
} from "./provider-authorization/provider-authorization";

/**
 * Checks if the provided error details correspond to a token expiration error.
 *
 * @param errorDetails - The error details object to check.
 * @returns `true` if the error is a recognized token expiration error, otherwise `false`.
 */
export function isTokenExpirationError(
  errorDetails: BcfBackendApiErrorDetails,
): boolean {
  return (
    errorDetails.title === BcfBackendApiErrorDetailsType.coreApiError &&
    KNOWN_BCF_BACKEND_CORE_API_ERROR_CODES.includes(errorDetails.status)
  );
}

/** Props for the ExternalAnnotationError component */
type ExternalAnnotationErrorProps = Omit<
  ProviderAuthorizationProps,
  "integrationType"
> &
  Partial<Pick<ProviderAuthorizationProps, "integrationType">> & {
    errorDetails: BcfBackendApiErrorDetails;
  };

/**
 * Displays an error message indicating that the session with the external annotation provider might have expired.
 *
 * @param bcfIntegrationType - The type of BCF services integration, or undefined if not available.
 * @returns A JSX element containing the error message.
 */
export function ExternalAnnotationError({
  integrationType,
  errorDetails,
  onTokenUpdated,
  onAuthorizationButtonClicked,
}: ExternalAnnotationErrorProps): JSX.Element {
  switch (errorDetails.title) {
    case BcfBackendApiErrorDetailsType.coreApiError: {
      if (isTokenExpirationError(errorDetails) && integrationType) {
        return (
          <>
            <FaroText variant="bodyM">
              {"Your session with "}
              <TranslateVar name="providerName">
                {getExternalAnnotationProviderName(integrationType)}
              </TranslateVar>
              {
                " might have expired. To continue, please refresh the connection by clicking the Authorize button."
              }
            </FaroText>
            <Box
              sx={{
                marginTop: "8px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <ProviderAuthorization
                integrationType={integrationType}
                onTokenUpdated={onTokenUpdated}
                onAuthorizationButtonClicked={onAuthorizationButtonClicked}
              />
            </Box>
          </>
        );
      }
      // If the error code is not recognized, return a generic error message
      break;
    }
    case BcfBackendApiErrorDetailsType.autodeskApiError:
    case BcfBackendApiErrorDetailsType.procoreApiError: {
      return (
        <FaroText variant="bodyM">
          Request to{" "}
          {integrationType ? (
            <TranslateVar name="providerName">
              {getExternalAnnotationProviderName(integrationType)}
            </TranslateVar>
          ) : (
            "external annotation provider"
          )}{" "}
          failed with error: {errorDetails.detail}.
        </FaroText>
      );
    }
  }
  // If the error title does not match any known types, return a generic error message
  return (
    <FaroText variant="bodyM">
      An unexpected error occurred while processing a request. Error details:{" "}
      {JSON.stringify(errorDetails)}.
    </FaroText>
  );
}
